import { useMutation, useQuery } from "convex/react";
import { ExternalLinkIcon, EyeIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import {
	Context,
	ContextContent,
	ContextContentBody,
	ContextContentFooter,
	ContextContentHeader,
	ContextInputUsage,
	ContextOutputUsage,
	ContextReasoningUsage,
	ContextTrigger,
} from "@/components/ai-elements/context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../../../convex/_generated/api";
import { getContextPolicy } from "../../../convex/agent/contextCompaction";
import { deriveThreadKind } from "../../../convex/lib/threadKind";
import { modelLabel } from "./chatModels";
import { buildContextMeterUsage } from "./contextUsage";

const KIND_LABEL: Record<ReturnType<typeof deriveThreadKind>, string> = {
	chat: "chat",
	email: "email",
	teams: "Teams",
	cli: "agent-cli",
	other: "agent",
};

export default function ChatHeader({
	clericalThreadId,
	model,
}: {
	clericalThreadId: string;
	model: string;
}) {
	const thread = useQuery(api.agent.queries.getChatThread, {
		clericalThreadId,
	});
	const usage = useQuery(api.agent.queries.getThreadUsage, {
		clericalThreadId,
	});
	const renameMutation = useMutation(api.agent.queries.renameChatThread);

	const [editing, setEditing] = useState(false);

	const lastTurn = usage?.lastTurn;
	const contextMeter = lastTurn ? buildContextMeterUsage(lastTurn) : undefined;
	const contextWindowTokens = lastTurn
		? getContextPolicy(lastTurn.model).contextWindowTokens
		: undefined;

	function commit(value: string) {
		const title = value.trim();
		if (title && title !== thread?.title) {
			void renameMutation({ clericalThreadId, title });
		}
		setEditing(false);
	}

	const derivedKind = deriveThreadKind(
		clericalThreadId,
		thread ? "chat" : undefined,
	);
	// Staff "viewing someone else's thread" banner. Chat rows: getChatThread only
	// returns non-owner rows for staff, doubling as the gate. Non-chat threads:
	// getChatThread returns null, so kind is derived from the URL instead.
	const showStaffBanner =
		(thread && !thread.isOwnedByViewer) || (!thread && derivedKind !== "chat");
	const bannerOwner = thread?.ownerName?.trim();

	return (
		<header className="flex h-12 shrink-0 items-center gap-3 border-b bg-background/80 px-4 pl-14 backdrop-blur">
			<div className="flex min-w-0 flex-1 items-center gap-2">
				{editing ? (
					<Input
						autoFocus
						defaultValue={thread?.title ?? ""}
						onBlur={(e) => commit(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								commit((e.target as HTMLInputElement).value);
							} else if (e.key === "Escape") {
								setEditing(false);
							}
						}}
						className="h-7 text-sm"
					/>
				) : (
					<button
						type="button"
						onClick={() => thread && setEditing(true)}
						className="truncate text-sm font-medium hover:text-foreground"
						disabled={!thread}
						title={thread ? "Click to rename" : "Untitled"}
					>
						{thread?.title ?? `${KIND_LABEL[derivedKind]} thread`}
					</button>
				)}
				{showStaffBanner ? (
					<span
						className="inline-flex shrink-0 items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning"
						title="Staff debug view — actions affect this user's thread."
					>
						<EyeIcon className="size-3" />
						Viewing {bannerOwner ? `${bannerOwner}'s ` : ""}
						{KIND_LABEL[derivedKind]} thread
					</span>
				) : null}
			</div>
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<span className="hidden sm:inline">{modelLabel(model)}</span>
				{lastTurn ? (
					<Context
						usedTokens={contextMeter?.usedTokens ?? 0}
						maxTokens={contextWindowTokens ?? 200_000}
						modelId={lastTurn.model}
						usage={contextMeter?.usage}
						costUSD={usage?.dollarCostUSD}
					>
						<ContextTrigger />
						<ContextContent>
							<ContextContentHeader />
							<ContextContentBody>
								<ContextInputUsage />
								<ContextOutputUsage />
								<ContextReasoningUsage />
							</ContextContentBody>
							<ContextContentFooter />
						</ContextContent>
					</Context>
				) : null}
				<Button
					variant="ghost"
					size="sm"
					nativeButton={false}
					className="h-7 gap-1 text-xs"
					render={
						<Link
							to={`/workspace/threads/${clericalThreadId}`}
							title="Open in workspace inspector"
						/>
					}
				>
					<ExternalLinkIcon className="size-3.5" />
				</Button>
			</div>
		</header>
	);
}
